<template>
  <section class="tournament-rounds" v-if="tournament && tournament.id">
    <!-- Auto Schedule Round -->
    <div
      class="is-flex is-justify-content-space-between mt-2"
      v-if="!isOfflineTournament"
    >
      <b-field>
        <b-button
          type="is-static"
          label="Tournament start"
          style="border: none"
        />
        <b-datetimepicker
          placeholder="Type or select a date..."
          editable
          icon="calendar"
          :disabled="sortedRounds[0] && !!sortedRounds[0].finishTime"
          :value="tournament ? tournament.startTime.toDate() : null"
          @input="(e) => setTournamentStartTime(e)"
        >
          <template #left>
            <b-button
              label="Now"
              type="is-primary"
              icon-left="clock"
              @click="setTournamentStartTime(new Date())"
            />
          </template>
        </b-datetimepicker>
      </b-field>

      <b-field>
        <b-button
          type="is-static"
          label="Round interval:"
          style="border: none"
        />
        <b-input
          style="width: 80px"
          type="number"
          :min="(tournament && tournament.baseTimeControl) || 15"
          v-model="roundInterval"
        ></b-input>
        <b-button type="is-static" label="minutes" style="border: none" />
        <b-button
          type="is-primary"
          label="Schedule"
          :disabled="tournament.currentRound >= tournament.totalRounds"
          @click="scheduleRounds"
        />
      </b-field>
    </div>

    <!-- Tab-based Interface -->
    <div class="">
      <div
        class="is-flex is-justify-content-space-between mt-4"
        style="border-bottom: 2px solid grey"
      >
        <!-- Round Selection Tabs (Left Side) -->
        <span>
          <b-field class="is-flex">
            <b-radio-button
              v-for="round in rounds.filter(
                (r) => r.roundNumber <= tournament.totalRounds
              )"
              :key="'round' + round.roundNumber"
              v-model="selectedRoundNumber"
              :native-value="round.roundNumber"
              :disabled="round.roundNumber > maxShownRoundNumber"
            >
              <div
                :class="
                  round.roundNumber === currentRoundNumber
                    ? 'has-text-success'
                    : ''
                "
              >
                <p>
                  <b>Round {{ round.roundNumber }}</b>
                </p>
                <p v-if="!isOfflineTournament">
                  {{ round.startTime | asTime }}
                </p>
              </div>
            </b-radio-button>
          </b-field>
        </span>

        <!-- Content Type Tabs (Right Side) -->
        <span>
          <b-field class="is-flex">
            <b-radio-button v-model="tabIndex" native-value="0">
              <div>
                <p><b>STANDINGS</b></p>
                <p>{{ players.length }} players</p>
              </div>
            </b-radio-button>

            <b-radio-button
              v-model="tabIndex"
              native-value="1"
              style="min-width: 106px"
            >
              <div>
                <p><b>GAMES</b></p>
                <p>{{ roundGames(selectedRoundNumber).length }} boards</p>
              </div>
            </b-radio-button>
          </b-field>
        </span>
      </div>

      <!-- Tab Content -->
      <div>
        <!-- Standings Tab -->
        <TournamentTableStandings
          v-if="tabIndex === '0'"
          :tournament="tournament"
          :rounds="sortedRounds"
          :players="sortedPlayers"
          :games="games"
          :roundNumber="selectedRoundNumber"
        />

        <!-- Games Tab -->
        <TournamentRoundDetails
          v-for="round in rounds"
          :key="`round-${round.roundNumber}`"
          v-show="tabIndex === '1' && selectedRoundNumber === round.roundNumber"
          :tournament="tournament"
          :rounds="sortedRounds"
          :games="games"
          :players="sortedPlayers"
          :round="round"
          :elapsed="getElapsedForRound(selectedRound)"
          @onAbort="abortRound"
          @onPair="pairRound"
          @onSave="saveRound"
          @onStart="startRound"
          @onStartUnstartedGames="startUnstartedGames"
          @onFixResults="fixResults"
          @onFinish="finishRound"
          @onUnfinish="unfinishRound"
          @onCopy="copyRound"
          @onStartGame="startGame"
          @onRematchGame="rematchGame"
        />
      </div>
    </div>
  </section>
</template>

<script>
import TournamentTableStandings from "@/components/admins/tournaments/TournamentTableStandings.vue";
import TournamentRoundDetails from "@/components/admins/tournaments/TournamentRoundDetails.vue";
import TournamentCommonData from "@/components/admins/tournaments/TournamentCommonData.js";
import Clock from "@/mixins/clock.js";
import { getJSONTournamentAtRound } from "vue-chess-shared/tournament/utils/tournamentUtils";
import { mapActions } from "vuex";

export default {
  components: {
    TournamentTableStandings,
    TournamentRoundDetails,
  },

  name: "TournamentRounds",

  props: ["tournament", "rounds", "games", "players"],

  mixins: [Clock, TournamentCommonData],

  data() {
    return {
      tabIndex: "1",
      selectedRoundNumber: 0,
      now: null,
      roundInterval: 30,
    };
  },

  computed: {
    isOfflineTournament() {
      // Check if this is an offline tournament by looking at the route
      // Offline tournaments are accessed via /teacher/class-tournaments/:id
      return this.$route.name === "OfflineClassTournament";
    },

    currentRoundNumber() {
      if (!this.tournament) return null;
      return this.tournament.currentRound;
    },

    selectedRound() {
      if (!this.sortedRounds || this.selectedRoundNumber === 0) return null;
      return this.sortedRounds[this.selectedRoundNumber - 1];
    },

    maxShownRoundNumber() {
      if (!this.currentRoundNumber) return 1;
      // if current row has finished show next round as well
      if (this.sortedRounds[this.currentRoundNumber - 1]?.finishTime) {
        return this.currentRoundNumber + 1;
      }
      return this.currentRoundNumber;
    },
  },

  watch: {
    tournament: {
      deep: true,
      immediate: true,
      handler() {
        if (!this.tournament) {
          return;
        }
        this.roundInterval = this.tournament.baseTimeControl * 2 + 2;
      },
    },

    selectedRoundNumber: {
      handler(newRoundNumber) {
        // Sync selected round number to Vuex
        this.updateClassTournamentState({
          tournamentId: this.tournament.id,
          selectedRoundNumber: newRoundNumber,
        });
      },
      immediate: true,
    },

    maxShownRoundNumber: {
      handler(newMaxRoundNumber) {
        this.selectedRoundNumber = newMaxRoundNumber;
      },
      immediate: true,
    },
  },

  methods: {
    ...mapActions(["updateClassTournamentState"]),

    /**
     * Get games for a specific round
     */
    roundGames(roundNumber) {
      return this.games
        .filter(
          (g) =>
            g.roundNumber === roundNumber &&
            (g.whiteUsername || g.blackUsername)
        )
        .sort((a, b) => a.board - b.board);
    },

    setTournamentStartTime(date) {
      this.$emit("onTournamentStartTimeChanged", date);
    },

    async scheduleRounds() {
      this.$emit("onScheduleRounds");
    },

    async changeRoundStartTime(round, date) {
      this.$emit("onRoundStartTimeChanged", round, date);
    },

    async pushRoundStartTime(round, minutes) {
      this.$emit("onRoundStartTimePushed", round, minutes);
    },

    async startRound(round) {
      this.$emit("onStart", round);
    },

    async abortRound(round) {
      this.$emit("onAbort", round);
    },

    async pairRound(round, callback) {
      this.$emit("onPair", round, callback);
    },

    async saveRound(round, roundGames, callback) {
      this.$emit("onSave", round, roundGames, callback);
    },

    async startUnstartedGames(round) {
      this.$emit("onStartUnstartedGames", round);
    },

    async fixResults(round) {
      this.$emit("onFixResults", round);
    },

    async finishRound(round) {
      this.$emit("onFinish", round);
    },

    async unfinishRound(round) {
      this.$emit("onUnfinish", round);
    },

    async startGame(round, game) {
      this.$emit("onStartGame", round, game);
    },

    async rematchGame(round, game) {
      this.$emit("onRematchGame", round, game);
    },

    async copyRound(round) {
      await navigator.clipboard.writeText(
        getJSONTournamentAtRound(
          this.tournament,
          this.players,
          this.games,
          this.rounds,
          round.roundNumber
        )
      );
      this.$buefy.toast.open({
        message: `Round state copied`,
        type: "is-info",
      });
    },

    onClockTick() {
      this.now = new Date();
    },

    getElapsedForRound(round) {
      const r = this.sortedRounds[round.roundNumber - 1];
      if (r && r.startTime) {
        if (r.finishTime && r.finishTime > r.startTime) {
          return r.finishTime - r.startTime;
        } else {
          return (this.now - r.startTime.toDate()) / 1000;
        }
      }
      return 0;
    },
  },
};
</script>

<style scoped></style>
